import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Container,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeviceModel from './steps/DeviceModel';
import SelectModel from './steps/SelectModel';
import UploadData from './steps/UploadData';
import SelectFlexTimerange from './steps/SelectFlexTimerange';
import CalculateFlexibility from './steps/CalculateFlexibility';

import InfoIcon from '@mui/icons-material/Info';
import {
  CustomConnector,
  CustomStepIconRoot,
  containerStyles,
  stepperStyles,
  tooltipIconStyles,
  AccountTreeIcon,
  InsertDriveFileIcon,
  AccessTimeIcon,
  EqualizerIcon,
  DevicesIcon
} from './CustomStepperStyles';

function CustomStepIcon(props) {
  const { active, completed, className, stepLabel } = props;

  // Dynamic icon mapping based on step content
  const getIconForStep = (label) => {
    switch (label) {
      case 'Select Forecasting Model':
        return <AccountTreeIcon />;
      case 'Insert Data':
        return <InsertDriveFileIcon />;
      case 'Select Device Model':
        return <DevicesIcon />;
      case 'Select Flexibility Time Range':
        return <AccessTimeIcon />;
      case 'Calculate Flexibility':
        return <EqualizerIcon />;
      default:
        return <AccountTreeIcon />;
    }
  };

  return (
    <CustomStepIconRoot ownerState={{ completed, active }} className={className}>
      {getIconForStep(stepLabel)}
    </CustomStepIconRoot>
  );
}

const CustomStepper = ({ activeStep, handleNext, handleBack, handleBackButton, selectedCountry, selectedOption, selectedUserId }) => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [fileSize, setFileSize] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flexibilityDays, setFlexibilityDays] = useState(0);
  const [modelName, setModelName] = useState('');
  const [modelDesc, setModelDesc] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [isNewModel, setIsNewModel] = useState(true);
  const [dataOption, setDataOption] = useState('keep');

  const isDisaggregated = selectedOption === 'Disaggregated';
  const isAggregated = selectedOption === 'Aggregated';

  const steps = [
    'Select Forecasting Model',
    'Insert Data',
    ...(isDisaggregated ? ['Select Devices'] : []),
    ...(isAggregated ? ['Select Flexibility Time Range'] : []),
    'Calculate Flexibility'
  ];

  const tooltipMessages = [
    'You may either select an existing model or create a new one. There is a limit of 10 models per user. These models are trained on the data you provide and are used to calculate flexibility. However, you can use a pretrained model and train it further with an additional dataset etc etc etc.',
    'Add data to the application by uploading a file from your computer. The file must be in CSV format and should not exceed 1GB in size.',
    ...(isDisaggregated ? ['Select specific devices to analyze.'] : []),
    ...(isAggregated ? ['Choose the time period used for model training and forecasting calculations. This defines the timeframe the model will analyze, not the window during which flexibility shifts occur.'] : []),
    ...(isAggregated ? ['Click on "CALCULATE" to see the Aggregated Data Analytics Dashboard, while the model gets trained in the background to produce the forecasts.'] : ['Click on "CALCULATE" to see the Disaggregated Data Analytics dashboard while the model gets trained in the background to produce the forecasts.']),
  ];

  const handleMoveToDash = () => {
    navigate('/dashboard');
  };

  // Handle model selection from SelectModel component
  const handleModelSelected = (modelData) => {
    setModelName(modelData.modelName);
    setModelDesc(modelData.modelDesc);
    setSelectedModel(modelData.modelName);
    setIsNewModel(modelData.isNewModel);
    if (modelData.isNewModel) {
      setDataOption('upload');
    }
  };

  // Handle device selection from DeviceModelStep component
  const handleDevicesSelected = (devices) => {
    setSelectedDevices(devices);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <SelectModel
            handleNext={handleNext}
            handleBackButton={handleBackButton}
            selectedCountry={selectedCountry}
            selectedOption={selectedOption}
            selectedUserId={selectedUserId}
            onModelSelected={handleModelSelected}
          />
        );

      case 1:
        return (
          <UploadData
            handleNext={handleNext}
            handleBack={handleBack}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            fileName={fileName}
            setFileName={setFileName}
            fileSize={fileSize}
            setFileSize={setFileSize}
            isSubmitting={isSubmitting}
            setIsSubmitting={setIsSubmitting}
            modelName={modelName}
            selectedCountry={selectedCountry}
            selectedOption={selectedOption}
            selectedUserId={selectedUserId}
            isNewModel={isNewModel}
            dataOption={dataOption}
            setDataOption={setDataOption}
          />
        );

      case 2:
        // For disaggregated: show device model step
        if (isDisaggregated) {
          return (
            <DeviceModel
              onNext={handleNext}
              onBack={handleBack}
              selectedDevices={selectedDevices}
              onDevicesSelected={handleDevicesSelected}
            />
          );
        }
        // For aggregated: show flexibility time range step
        if (isAggregated) {
          return (
            <SelectFlexTimerange
              handleNext={handleNext}
              handleBack={handleBack}
              flexibilityDays={flexibilityDays}
              setFlexibilityDays={setFlexibilityDays}
            />
          );
        }
        return null;

      case 3:
        // For both disaggregated and aggregated: show calculate step
        return (
          <CalculateFlexibility
            handleBack={handleBack}
            modelName={modelName}
            modelDesc={modelDesc}
            selectedCountry={selectedCountry}
            selectedOption={selectedOption}
            selectedUserId={selectedUserId}
            flexibilityDays={flexibilityDays}
            fileName={fileName}
            fileSize={fileSize}
            selectedDevices={selectedDevices}
            dataOption={dataOption}
          />
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Container maxWidth="lg" sx={containerStyles}>
      <Stepper
        activeStep={activeStep}
        orientation="vertical"
        connector={<CustomConnector />}
        sx={stepperStyles}
      >
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel
              StepIconComponent={(props) => <CustomStepIcon {...props} stepLabel={label} />}
            >
              {label}
              <Tooltip title={tooltipMessages[index]} placement="right">
                <IconButton size="small" sx={tooltipIconStyles}>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </StepLabel>
            <StepContent>{getStepContent(index)}</StepContent>
          </Step>
        ))}
      </Stepper>
    </Container>
  );
};

export default CustomStepper;
